import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo, useState } from "react";

import { useAppDispatch } from "app/hooks";
import { useAppSelector } from "app/hooks";
import { error, misc, neutral, success } from "app/themes/colors";
import {
  setContent,
  setOnChange,
  setOpen,
  setTitle
} from "components/confirm-dialog/confirm-dialog.slice";
import StatusTransitionCell, {
  DisabledStatusTransitionFlow,
  StatusTransitionCode
} from "components/status-transition-cell/status-transition-cell.component";
import {
  ENFORCE_DELIVERY_BEFORE_READY_TO_BILL,
  MULTIPLE_CPT_CODES_FEATURE,
  SALE_QUOTE
} from "constants/feature-flags";
import {
  SALE_CANCELED_STATUS_TYPE,
  SALE_COMPLETED_STATUS_TYPE,
  SALE_DRAFT_STATUS_TYPE,
  SALE_IN_BILLING_STATUS_TYPE,
  SALE_IN_COLLECTION_STATUS_TYPE,
  SALE_QUOTE_STATUS_TYPE,
  SALE_READY_TO_BILL_STATUS_TYPE,
  SALE_STATUSES
} from "constants/sales";
import {
  ACCESSORY_PRODUCT_TYPE,
  HEARING_AID_PRODUCT_TYPE,
  PRODUCT_TYPES,
  SUPPLY_PRODUCT_TYPE
} from "constants/sales";
import {
  selectCurrentPractice,
  selectCurrentUser
} from "features/auth/auth-slice";
import useUserRoles from "hooks/use-user-roles.hook";
import QuoteConversionDialog from "features/billing/components/quote-conversion-dialog.component";
import { useGetSaleItemsQuery } from "services/sales";
import { SaleItem } from "services/types/sale-items.type";
import { Sale, SaleInput } from "services/types/sales.type";

export const SALE_STATUS_COLORS: Record<number, string> = {
  [SALE_QUOTE_STATUS_TYPE]: misc.selectedMenuitem,
  [SALE_DRAFT_STATUS_TYPE]: misc.grayAsphalt,
  [SALE_READY_TO_BILL_STATUS_TYPE]: error[400],
  [SALE_IN_BILLING_STATUS_TYPE]: misc.darkYellow,
  [SALE_IN_COLLECTION_STATUS_TYPE]: error[900],
  [SALE_COMPLETED_STATUS_TYPE]: success[600],
  [SALE_CANCELED_STATUS_TYPE]: neutral[500]
};

const SALE_STATUS_TRANSITIONS = {
  [SALE_QUOTE_STATUS_TYPE]: [
    SALE_DRAFT_STATUS_TYPE,
    SALE_READY_TO_BILL_STATUS_TYPE,
    SALE_IN_BILLING_STATUS_TYPE,
    SALE_IN_COLLECTION_STATUS_TYPE,
    SALE_COMPLETED_STATUS_TYPE,
    SALE_CANCELED_STATUS_TYPE
  ],
  [SALE_DRAFT_STATUS_TYPE]: [
    SALE_READY_TO_BILL_STATUS_TYPE,
    SALE_IN_BILLING_STATUS_TYPE,
    SALE_COMPLETED_STATUS_TYPE,
    SALE_CANCELED_STATUS_TYPE
  ],
  [SALE_READY_TO_BILL_STATUS_TYPE]: [
    SALE_DRAFT_STATUS_TYPE,
    SALE_IN_BILLING_STATUS_TYPE,
    SALE_COMPLETED_STATUS_TYPE,
    SALE_CANCELED_STATUS_TYPE
  ],
  [SALE_IN_BILLING_STATUS_TYPE]: [
    SALE_DRAFT_STATUS_TYPE,
    SALE_READY_TO_BILL_STATUS_TYPE,
    SALE_COMPLETED_STATUS_TYPE,
    SALE_CANCELED_STATUS_TYPE
  ],
  [SALE_COMPLETED_STATUS_TYPE]: [
    SALE_DRAFT_STATUS_TYPE,
    SALE_READY_TO_BILL_STATUS_TYPE,
    SALE_IN_BILLING_STATUS_TYPE
  ]
};

const CONFIRMATION_REQUIRED_STATUS_TRANSITIONS: Set<StatusTransitionCode> =
  new Set([SALE_COMPLETED_STATUS_TYPE, SALE_CANCELED_STATUS_TYPE]);

type SaleStatusCellProps = {
  testId: string;
  sale: Sale;
  design?: "rounded" | "squared";
  onUpdateSale?: (sale: Partial<SaleInput>) => Promise<any>;
  // Called after a quote was converted to a real invoice via the conversion
  // popup. Lists using accumulated pagination (usePaginationV2) must re-fetch
  // the sale's page here — tag invalidation alone won't refresh them (SN-18215)
  onQuoteConverted?: (saleId: number) => void;
  readonly?: boolean;
};
export default function SaleStatusCell({
  testId,
  sale,
  onUpdateSale,
  onQuoteConverted,
  design = "rounded",
  readonly = false
}: SaleStatusCellProps) {
  const dispatch = useAppDispatch();
  const [menuOpen, setMenuOpen] = useState(false);
  const [enforceItemsToBeDelivered, setEnforceItemsToBeDelivered] =
    useState(false);
  // Target status picked from the dropdown while the sale is a quote — opens the
  // quote conversion popup (SN-18215)
  const [conversionTargetStatus, setConversionTargetStatus] = useState<
    number | null
  >(null);

  const flags = useFlags();
  const enforceDeliveryBeforeReadyToBillFlag =
    flags[ENFORCE_DELIVERY_BEFORE_READY_TO_BILL];
  const saleQuoteEnabled = flags[SALE_QUOTE];
  const multipleCptCodesFeature = Boolean(flags[MULTIPLE_CPT_CODES_FEATURE]);
  const currentPractice = useAppSelector(selectCurrentPractice);
  const currentUser = useAppSelector(selectCurrentUser);
  const canDeleteInvoices = Boolean(currentUser?.able_to_delete_invoices);
  const enforceDeliveryBeforeReadyToBill =
    currentPractice?.enforce_delivery_before_ready_to_bill ||
    enforceDeliveryBeforeReadyToBillFlag;

  const ownedBalance = parseFloat((sale?.balance ?? "0").toString());

  const { data: saleItems } = useGetSaleItemsQuery(
    { saleId: Number(sale?.id), multipleCptCodesFeature },
    { skip: readonly || !menuOpen || Number.isNaN(Number(sale?.id)) }
  );
  const saleItemsCount = saleItems?.length ?? 0;

  const { isAdminOrBusinessOwner } = useUserRoles();

  useEffect(() => {
    if (enforceDeliveryBeforeReadyToBill) {
      const deliveryRequiredTypes = [
        HEARING_AID_PRODUCT_TYPE,
        ACCESSORY_PRODUCT_TYPE,
        SUPPLY_PRODUCT_TYPE,
        PRODUCT_TYPES
      ];

      const hasUndeliveredItems =
        saleItems?.some(
          (saleItem: SaleItem) =>
            deliveryRequiredTypes.includes(
              saleItem.product.master_product.type
            ) && !saleItem.delivered_at
        ) ?? false;

      setEnforceItemsToBeDelivered(hasUndeliveredItems);
    } else {
      setEnforceItemsToBeDelivered(false);
    }
  }, [saleItems, enforceDeliveryBeforeReadyToBill]);

  const statusColors = useMemo(() => {
    // If feature flag is off, remove quote color EXCEPT for existing quotes
    if (!saleQuoteEnabled) {
      // If this sale is a quote, keep the quote color for display
      if (Number(sale?.status) === SALE_QUOTE_STATUS_TYPE) {
        return SALE_STATUS_COLORS;
      }
      // Otherwise, remove quote color so it won't appear in dropdowns
      const { [SALE_QUOTE_STATUS_TYPE]: _, ...colors } = SALE_STATUS_COLORS;
      return colors;
    }
    return SALE_STATUS_COLORS;
  }, [saleQuoteEnabled, sale?.status]);

  const statusTransitions = useMemo(() => {
    const transitions = isAdminOrBusinessOwner
      ? {
          ...SALE_STATUS_TRANSITIONS,
          [SALE_CANCELED_STATUS_TYPE]: [
            SALE_DRAFT_STATUS_TYPE,
            SALE_READY_TO_BILL_STATUS_TYPE,
            SALE_IN_BILLING_STATUS_TYPE,
            SALE_COMPLETED_STATUS_TYPE
          ]
        }
      : SALE_STATUS_TRANSITIONS;

    // If feature flag is off, remove quote transitions EXCEPT for existing quotes
    // This allows users to transition existing quotes to other statuses
    if (!saleQuoteEnabled && Number(sale?.status) !== SALE_QUOTE_STATUS_TYPE) {
      if (transitions[SALE_QUOTE_STATUS_TYPE]) {
        delete transitions[SALE_QUOTE_STATUS_TYPE];
      }
    }

    return transitions;
  }, [isAdminOrBusinessOwner, saleQuoteEnabled, sale?.status]);

  const disabledStatusTransitions = {
    ...(saleItemsCount === 0 && {
      [SALE_DRAFT_STATUS_TYPE]: {
        [SALE_READY_TO_BILL_STATUS_TYPE]:
          "Please add line items to the invoice before changing status"
      }
    }),
    ...(!Boolean(sale?.service_date) && {
      [SALE_DRAFT_STATUS_TYPE]: {
        [SALE_READY_TO_BILL_STATUS_TYPE]:
          "Invoice has no Service Date. This invoice cannot be billed until a Fitting appointment has occurred.",
        [SALE_COMPLETED_STATUS_TYPE]:
          "Invoice has no Service Date. This invoice cannot be completed until a Fitting appointment has occurred."
      },
      [SALE_COMPLETED_STATUS_TYPE]: {
        [SALE_READY_TO_BILL_STATUS_TYPE]:
          "Invoice has no Service Date. This invoice can only be changed to Draft status.",
        [SALE_IN_BILLING_STATUS_TYPE]:
          "Invoice has no Service Date. This invoice can only be changed to Draft status."
      }
    }),
    ...(enforceItemsToBeDelivered && {
      [SALE_DRAFT_STATUS_TYPE]: {
        [SALE_READY_TO_BILL_STATUS_TYPE]: "Please mark all items as delivered."
      }
    }),
    ...(!canDeleteInvoices && {
      [SALE_DRAFT_STATUS_TYPE]: {
        [SALE_CANCELED_STATUS_TYPE]: "You don't have permissions"
      },
      [SALE_READY_TO_BILL_STATUS_TYPE]: {
        [SALE_CANCELED_STATUS_TYPE]: "You don't have permissions"
      },
      [SALE_IN_BILLING_STATUS_TYPE]: {
        [SALE_CANCELED_STATUS_TYPE]: "You don't have permissions"
      },
      [SALE_COMPLETED_STATUS_TYPE]: {
        [SALE_CANCELED_STATUS_TYPE]: "You don't have permissions"
      }
    })
  };

  const isStatusReadonly =
    readonly ||
    (Number(sale?.status) === SALE_IN_COLLECTION_STATUS_TYPE &&
      sale?.status !== SALE_QUOTE_STATUS_TYPE) ||
    (!Boolean(sale?.service_date) &&
      sale?.status !== SALE_COMPLETED_STATUS_TYPE &&
      sale?.status !== SALE_QUOTE_STATUS_TYPE);

  const handleChange = (status: StatusTransitionCode) => {
    // Converting a quote into a real sale needs sourcing decisions (From Stock /
    // Place Order) and a service date, so open the conversion popup instead of a
    // plain status change. Canceling a quote stays a plain status change. Gated by
    // the saleQuote flag: with the flag off (or the convert-quote endpoint not yet
    // deployed) quotes fall back to the plain status update. (SN-18215)
    if (
      saleQuoteEnabled &&
      Number(sale?.status) === SALE_QUOTE_STATUS_TYPE &&
      status !== SALE_CANCELED_STATUS_TYPE
    ) {
      setConversionTargetStatus(Number(status));
      return Promise.resolve();
    }
    if (CONFIRMATION_REQUIRED_STATUS_TRANSITIONS.has(status)) {
      dispatch(
        setTitle(
          `Are you sure you want to ${
            status === SALE_COMPLETED_STATUS_TYPE
              ? "complete this Invoice"
              : "change the Invoice status"
          }?`
        )
      );
      dispatch(
        setOnChange(
          () =>
            onUpdateSale &&
            onUpdateSale({
              id: Number(sale?.id),
              status: status.toString()
            })
        )
      );
      dispatch(setOpen(true));
      const warnings =
        ownedBalance > 0 ? "The invoice is not yet balanced." : "";

      dispatch(
        setContent(
          `${warnings} You can always change the status to draft.`.trim()
        )
      );
      return Promise.resolve();
    } else {
      if (onUpdateSale) {
        return onUpdateSale({
          id: Number(sale?.id),
          status: status.toString()
        });
      } else {
        return Promise.resolve();
      }
    }
  };
  return (
    <>
      <StatusTransitionCell
        testId={testId}
        status={Number(sale?.status)}
        statusColors={statusColors}
        allStatuses={SALE_STATUSES}
        statusTransitions={statusTransitions}
        disabledStatusTransitions={
          disabledStatusTransitions as DisabledStatusTransitionFlow
        }
        onChange={handleChange}
        readonly={isStatusReadonly}
        design={design}
        onMenuOpen={(isOpen) => setMenuOpen(isOpen)}
      />
      {conversionTargetStatus !== null && (
        <QuoteConversionDialog
          open
          saleId={Number(sale?.id)}
          patientId={sale?.patient?.id}
          initialStatus={conversionTargetStatus}
          onClose={() => setConversionTargetStatus(null)}
          onConverted={() => {
            setConversionTargetStatus(null);
            onQuoteConverted?.(Number(sale?.id));
          }}
        />
      )}
    </>
  );
}
